import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../assets/icons/LineDot";
import {
  ChartTitle,
  StyledSubtitle,
  StyledTile,
} from "../../../../../../components/ChartCard/ChartCard.styles";
import { ChartCardValue } from "../../../../../../components/ChartCard/ChartCard.types";
import { ChartCardLoading } from "../../../../../../components/ChartCard/ChartCardLoading";
import { ChartSpecificValues } from "../../../../../../components/ChartCard/ChartSpecificValues/ChartSpecificValues";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { ChartLegendContainer } from "../../../../../../components/ModalChart/CustomModalChartLegend/CustomModalChartLegend.style";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { getFriendlySourceName } from "../../../../../../utils/chartSourceUtils";
import { DATE_FILTER_ALL } from "../../../MeasurementModal/components/CustomOptionsBar";
import {
  aggregateStepsByWeek,
  buildStepsChartData,
  getStepsSourceColor,
  StepsBarChart,
} from "../../../MeasurementModal/components/StepsBarChart";
import {
  GraphContainer,
  GraphLegend,
  SourcesColumn,
  StepsChartDataContainer,
} from "./StepsChartCard.styles";

type StepsChartCardProps = {
  title: string;
  data: Map<string, any[]>;
  specificValues: ChartCardValue[];
  isLoading?: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
};

export function StepsChartCard({
  title,
  data,
  specificValues,
  isLoading,
  onClick,
}: StepsChartCardProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter } = useUserPreferences();
  const dateRangeFilter = getDateRangeFilter();

  const dailyData = useMemo(() => buildStepsChartData(data), [data]);

  const weeklyAggregation = useMemo(() => {
    if (dailyData.rows.length === 0) return false;
    if (dateRangeFilter.id === DATE_FILTER_ALL) {
      const minTs = Math.min(...dailyData.rows.map((r) => r.timeValue));
      const maxTs = Math.max(...dailyData.rows.map((r) => r.timeValue));
      return dayjs(maxTs).diff(dayjs(minTs), "month") > 6;
    }
    return dateRangeFilter.endDate.diff(dateRangeFilter.startDate, "month") > 6;
  }, [dailyData.rows, dateRangeFilter]);

  const { rows: chartData, sources } = useMemo(
    () => (weeklyAggregation ? aggregateStepsByWeek(dailyData) : dailyData),
    [dailyData, weeklyAggregation]
  );

  const isEmptyState = chartData.length === 0;

  return (
    <StyledTile onClick={onClick}>
      {isLoading ? (
        <ChartCardLoading />
      ) : (
        <>
          <ChartTitle>{title}</ChartTitle>
          {isEmptyState ? (
            <StyledSubtitle>{t("patientPage_card_noData")}</StyledSubtitle>
          ) : null}

          <StepsChartDataContainer isEmpty={isEmptyState}>
            <GraphContainer isEmpty={isEmptyState}>
              {!isEmptyState ? (
                <GraphLegend>
                  <ChartLegendContainer>
                    <SourcesColumn>
                      {sources.map((source, idx) => (
                        <StyledLegend key={source}>
                          <LineDot fill={getStepsSourceColor(source, idx)} />
                          {getFriendlySourceName(source, t)}
                        </StyledLegend>
                      ))}
                    </SourcesColumn>
                  </ChartLegendContainer>
                </GraphLegend>
              ) : null}
              <StepsBarChart
                data={chartData}
                sources={sources}
                dateInterval={dateRangeFilter}
                height="90%"
                width="100%"
                onlyGraph
                weeklyAggregation={weeklyAggregation}
              />
            </GraphContainer>

            <ChartSpecificValues
              values={specificValues}
              isEmpty={isEmptyState}
            />
          </StepsChartDataContainer>
        </>
      )}
    </StyledTile>
  );
}
